import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useRef, useState } from "react";
import { Card, type SelectOption, Select, type SelectComponent, SettingsLayout } from "@clintonelec/react-storybook";
import { useAppSelector, useAppDispatch } from "Data/Redux/Store";
import { TamperDetection } from "Data/Objects/Camera";
import { selectTamperDetection, setTamperDetectionAction } from "Data/Redux/Slices/Settings/Camera/Tamper";
import ActionButtons from "Components/ConfigLayout/ActionButtons";

const tamperOptions: SelectOption[] = [
	{
		label: "Off",
		value: TamperDetection.OFF
	},
	{
		label: "Low",
		value: TamperDetection.LOW
	},
	{
		label: "Mid",
		value: TamperDetection.MID
	},
	{
		label: "High",
		value: TamperDetection.HIGH
	}
];

type TamperDetectionForm = {
	tamperSelect: SelectComponent;
} & HTMLFormElement;

function TamperSettings() {
	const dispatch = useAppDispatch();
	const selectRef = useRef<SelectComponent>(null);
	const tamperDetection = useAppSelector(selectTamperDetection);
	const [ localTamper, setLocalTamper ] = useState(tamperDetection);
	const setTamperDetection = (newDetection: TamperDetection) => dispatch(setTamperDetectionAction(newDetection));

	const handleFormSubmit = (event: ReactSubmitEvent<TamperDetectionForm>) => {
		event.preventDefault();

		const { tamperSelect: { value: newDetection } } = event.currentTarget;

		setTamperDetection(newDetection);
	};

	const handleReset = (event: FormEvent<TamperDetectionForm>) => {
		event.preventDefault();

		selectRef.current?.setValue(
			tamperOptions.find(option => option.value === tamperDetection)
		);
	};

	const handleSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalTamper(value as TamperDetection);
	};

	return (
		<SettingsLayout.Form
			noValidate
			onReset={ handleReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="small" title="Tamper Detection">
					<div className="form-row">
						<span>Level</span>
						<Select
							name="tamperSelect"
							allowClear={ false }
							className="form-select"
							options={ tamperOptions }
							defaultValue={ tamperDetection }
							forwardRef={ selectRef }
							onSelect={ handleSelect }
						/>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons
				disabled={ localTamper === tamperDetection }
			/>
		</SettingsLayout.Form>
	);
}

export default memo(TamperSettings);
